import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateGlobalSurgicalPeriodRulesetMutation,
  useGetGlobalSurgicalPeriodRulesetByIdQuery,
} from "store/queries/globalSurgicalPeriodRulesets";

import {
  IGlobalSurgicalPeriodFormData,
  INITIAL_GLOBAL_SURGICAL_PERIOD_FORM_DATA,
  GlobalSurgicalPeriodForm,
} from "./components/GlobalSurgicalPeriodForm";

export function CopyGlobalSurgicalPeriodPage() {
  useDocumentTitle("Copy Global Surgical Period Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } =
    useGetGlobalSurgicalPeriodRulesetByIdQuery(id ?? "");
  const [createMutation, { isLoading: isCreating }] =
    useCreateGlobalSurgicalPeriodRulesetMutation();
  const [formData, setFormData] = useState<IGlobalSurgicalPeriodFormData>(
    INITIAL_GLOBAL_SURGICAL_PERIOD_FORM_DATA
  );

  // A copy is a fresh create aimed at a new provider/payer, so it seeds as a
  // SCOPED rule: a SCOPED source carries its provider/payer over as a starting
  // point, while TYPICAL/FALLBACK sources have no scope to carry and start
  // empty. CLE-2524: the radio group stays editable here — this only seeds it.
  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    const sourceIsScoped = ruleset.applicability === "SCOPED";
    setFormData({
      groupName: ruleset.groupName ?? "",
      providerId: sourceIsScoped ? ruleset.providerId : null,
      payerId: sourceIsScoped ? ruleset.payerId : null,
      applicability: "SCOPED",
      lookbackDays: ruleset.lookbackDays,
      requireGlobalRemark: ruleset.requireGlobalRemark,
      contractLanguage: ruleset.contractLanguage ?? "",
    });
  }, [data]);

  const handleSubmit = async () => {
    if (formData.applicability === "SCOPED" && !formData.providerId) return;
    setIsLoading(true);
    try {
      await createMutation({
        groupName: formData.groupName.trim() || null,
        applicability: formData.applicability,
        providerId:
          formData.applicability === "SCOPED" ? formData.providerId : null,
        payerId: formData.applicability === "TYPICAL" ? null : formData.payerId,
        lookbackDays: formData.lookbackDays,
        requireGlobalRemark: formData.requireGlobalRemark,
        contractLanguage: formData.contractLanguage.trim() || null,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Global surgical period rule copied successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to copy global surgical period rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <GlobalSurgicalPeriodForm
      title="Copy Global Surgical Period Rule"
      submitLabel="Create Copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isCreating}
    />
  );
}
